import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

interface CteRow {
  emp: number
  chave: string
  serie: string
  numero: number
  modelo: string
  cnpj_emitente: string
  nome_emitente: string
  cnpj_tomador: string
  nome_tomador: string
  papel_tomador: string
  papel_empresa: string
  emissao: string
  valor: string
  situacao: string
  digitada: boolean
  digitada_em: string | null
  xml_existe: boolean
  desacordo: boolean
}

interface Filtros {
  periodoDe: string
  periodoAte: string
  situacao: string
  emitente: string
  tomador: string
  chave: string
  papelEmpresa: string
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.82rem', height: 30, background: '#fff',
}

const label: React.CSSProperties = {
  fontSize: '0.72rem', color: '#6b7280', fontWeight: 600,
}

function campo(rotulo: string, children: React.ReactNode) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

function selo(ativo: boolean) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
      fontSize: '0.74rem', fontWeight: 600,
      background: ativo ? '#dcfce7' : '#fee2e2',
      color: ativo ? '#15803d' : '#dc2626',
    }}>
      {ativo ? 'Sim' : 'Não'}
    </span>
  )
}

const dividerStyle: React.CSSProperties = {
  width: 1, background: '#e2e8f0', alignSelf: 'stretch',
}

const thBase: React.CSSProperties = {
  padding: '6px 4px', whiteSpace: 'nowrap', color: '#334155', fontWeight: 700,
}

function stat(rotulo: string, valor: string | number, cor: string) {
  return (
    <div style={{ textAlign: 'center', minWidth: 90 }}>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {rotulo}
      </div>
      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums' }}>
        {valor}
      </div>
    </div>
  )
}

function IconChave() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L21 7l-3-3m-3.5 3.5L17 4" />
    </svg>
  )
}

const SIT_BG: Record<string, string> = {
  Autorizada:   '#dcfce7',
  Denegada:     '#fee2e2',
  Cancelada:    '#f3f4f6',
  'A Consultar': '#fef3c7',
}
const SIT_COLOR: Record<string, string> = {
  Autorizada:   '#15803d',
  Denegada:     '#dc2626',
  Cancelada:    '#6b7280',
  'A Consultar': '#b45309',
}

function fmtMoeda(v: string | number) {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtData(iso: string) {
  return iso.slice(0, 10).split('-').reverse().join('/')
}

function fmtDataHora(iso: string) {
  // ntv_xmldhd é "timestamp without time zone" — o node-pg lê o valor como se
  // fosse horário local e devolve serializado em UTC (soma 3h de diferença pro
  // Brasil). Convertendo com Date/toLocaleString ele volta pro horário certo.
  const d = new Date(iso)
  const data = d.toLocaleDateString('pt-BR')
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${data} ${hora}`
}

function fmtCnpj(doc: string) {
  const n = (doc || '').replace(/\D/g, '').padStart(14, '0')
  return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

function ToolbarBtn({ title, bg, fg, border, children, disabled, destaque, onClick }: {
  title: string; bg: string; fg: string; border?: string; children: React.ReactNode; disabled?: boolean
  destaque?: boolean; onClick?: () => void
}) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `1px solid ${disabled ? '#e5e7eb' : destaque ? '#1a6ea8' : border ?? '#e2e8f0'}`,
        background: disabled ? '#f1f5f9' : destaque ? '#1a6ea8' : bg,
        color: disabled ? '#cbd5e1' : destaque ? '#fff' : fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: destaque ? '0 0 0 4px rgba(26,110,168,0.25), 0 2px 6px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.06)',
        transform: destaque ? 'scale(1.08)' : undefined,
        transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
      }}
    >
      {children}
    </button>
  )
}

const iconProps = {
  width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
}

function IconAtualizar() {
  return (
    <svg {...iconProps}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  )
}

function IconDesacordo() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12" y2="17.01" />
    </svg>
  )
}

function IconDownload() {
  return (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function IconPdf() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

function IconImportar() {
  return (
    <svg {...iconProps}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 8 12 3 17 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function IconDisponibilidade() {
  return (
    <svg {...iconProps}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  )
}

function filtroPadrao(): Filtros {
  const hoje = new Date()
  const inicio = new Date(hoje)
  inicio.setDate(inicio.getDate() - 15)
  return {
    periodoDe: inicio.toISOString().slice(0, 10),
    periodoAte: hoje.toISOString().slice(0, 10),
    situacao: '',
    emitente: '',
    tomador: '',
    chave: '',
    papelEmpresa: '',
  }
}

// Vindo do painel de Compras: ?de=&ate=&situacao=&digitada=nao&papel=tomador
const urlParams = () => new URLSearchParams(window.location.search)
function filtroInicial(): Filtros {
  const q = urlParams(), p = filtroPadrao()
  return {
    ...p, periodoDe: q.get('de') || p.periodoDe, periodoAte: q.get('ate') || p.periodoAte,
    situacao: q.get('situacao') ?? p.situacao, papelEmpresa: q.get('papel') ?? p.papelEmpresa,
  }
}

export default function ManifestadorCte() {
  const { empresaCod, empresaAtual } = useEmpresa()
  const navigate = useNavigate()
  const [filtros, setFiltros] = useState<Filtros>(filtroInicial)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroInicial)
  const [ctes, setCtes] = useState<CteRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set())
  const [chaveConsulta, setChaveConsulta] = useState('')
  const [filtroDigitada, setFiltroDigitada] = useState<'todas' | 'sim' | 'nao'>(() => urlParams().get('digitada') === 'nao' ? 'nao' : 'todas')
  const [atualizando, setAtualizando] = useState(false)
  const [msgAtualizar, setMsgAtualizar] = useState<{ texto: string; ok: boolean } | null>(null)

  // "Imprimir DACTE" gera o PDF direto do XML já capturado pela Distribuição
  // DF-e — não precisa o CT-e já estar digitado.
  function imprimirDacteSelecionado() {
    if (selecionados.size !== 1) {
      setMsgAtualizar({ texto: 'Selecione exatamente um CT-e pra imprimir o DACTE.', ok: false })
      return
    }
    const chave = [...selecionados][0]
    const cte = ctes.find(n => n.chave === chave)
    if (!cte) return
    if (!cte.xml_existe) {
      setMsgAtualizar({ texto: 'O XML desse CT-e ainda não foi baixado pela Distribuição DF-e.', ok: false })
      return
    }
    window.open(`/api/manifestador-cte/${chave}/dacte`, '_blank')
  }

  // "Importar XML" só faz sentido pra um CT-e de cada vez, ainda não digitado
  // e já Autorizado (mesma regra do Btn_Imp do Manifestador legado — Event 'Importar').
  function importarSelecionado() {
    if (selecionados.size !== 1) {
      setMsgAtualizar({ texto: 'Selecione exatamente um CT-e (ainda não digitado) pra importar.', ok: false })
      return
    }
    const chave = [...selecionados][0]
    const cte = ctes.find(n => n.chave === chave)
    if (!cte) return
    if (cte.digitada) {
      setMsgAtualizar({ texto: 'Esse CT-e já foi digitado — abra pela Consulta de Notas pra alterar.', ok: false })
      return
    }
    if (cte.situacao !== 'Autorizada') {
      setMsgAtualizar({ texto: 'Só é possível importar CT-e Autorizado.', ok: false })
      return
    }
    navigate(`/compras/recebimentos/lancamento-cte57?importarChave=${chave}`)
  }

  const [consultandoStatus, setConsultandoStatus] = useState(false)

  async function consultarDisponibilidade() {
    if (consultandoStatus) return
    setConsultandoStatus(true)
    setMsgAtualizar(null)
    try {
      const r = await api.get('/cte-os/status-servico')
      const d = r.data
      if (d.erro) {
        setMsgAtualizar({ texto: 'Não foi possível falar com a SEFAZ agora. Tente de novo em alguns instantes.', ok: false })
      } else if (d.emOperacao) {
        setMsgAtualizar({ texto: '✅ Servidor da Receita (RS) está funcionando normalmente pra CT-e. Pode emitir/transmitir sem problemas.', ok: true })
      } else if (d.cStat === '108' || d.cStat === '109') {
        setMsgAtualizar({ texto: '⚠ O servidor da Receita (RS) está fora do ar no momento. Aguarde um pouco e tente de novo.', ok: false })
      } else {
        setMsgAtualizar({ texto: `⚠ A Receita não confirmou que está tudo certo${d.xMotivo ? ` (${d.xMotivo})` : ''}. Tente de novo em alguns instantes.`, ok: false })
      }
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao consultar disponibilidade da SEFAZ (CT-e).', ok: false })
    } finally {
      setConsultandoStatus(false)
    }
  }

  const carregar = useCallback(async () => {
    if (!empresaCod) return
    setCarregando(true)
    setErro(null)
    try {
      const params: Record<string, string> = { empCod: String(empresaCod) }
      if (pesquisaAtiva.periodoDe)  params.dataInicio = pesquisaAtiva.periodoDe
      if (pesquisaAtiva.periodoAte) params.dataFim    = pesquisaAtiva.periodoAte
      if (pesquisaAtiva.situacao)   params.situacao   = pesquisaAtiva.situacao
      if (pesquisaAtiva.emitente)   params.emitente   = pesquisaAtiva.emitente
      if (pesquisaAtiva.tomador)    params.tomador    = pesquisaAtiva.tomador
      if (pesquisaAtiva.chave)      params.chave      = pesquisaAtiva.chave
      if (pesquisaAtiva.papelEmpresa) params.papelEmpresa = pesquisaAtiva.papelEmpresa
      const r = await api.get('/manifestador-cte', { params })
      setCtes(r.data)
      setSelecionados(new Set())
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [empresaCod, pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p); setFiltroDigitada('todas') }

  async function atualizarLista() {
    if (!empresaCod || atualizando) return
    setAtualizando(true)
    setMsgAtualizar(null)
    try {
      const r = await api.post('/manifestador-cte/atualizar', { empCod: empresaCod })
      const res = r.data
      if (res.erro) {
        setMsgAtualizar({ texto: res.erro, ok: false })
      } else if (res.cStat === '138') {
        setMsgAtualizar({ texto: `${res.novas} CT-e(s) novo(s) encontrado(s).`, ok: true })
      } else if (res.cStat === '137') {
        setMsgAtualizar({ texto: 'Nenhum documento novo encontrado.', ok: true })
      } else if (res.cStat === '656') {
        setMsgAtualizar({ texto: 'SEFAZ rejeitou por consumo indevido (cStat 656) — aguarde antes de consultar de novo.', ok: false })
      } else {
        setMsgAtualizar({ texto: res.cStat ? `cStat ${res.cStat}: ${res.xMotivo}` : 'Sem resposta da SEFAZ', ok: false })
      }
      await carregar()
    } catch (e: any) {
      setMsgAtualizar({ texto: e?.response?.data?.error || 'Erro ao atualizar lista', ok: false })
    } finally {
      setAtualizando(false)
    }
  }

  function toggleSel(chave: string) {
    setSelecionados(s => {
      const novo = new Set(s)
      if (novo.has(chave)) novo.delete(chave); else novo.add(chave)
      return novo
    })
  }

  // Marcou só um CT-e: já joga a chave no campo de baixo, pra poder copiar.
  useEffect(() => {
    setChaveConsulta(selecionados.size === 1 ? [...selecionados][0] : '')
  }, [selecionados])

  const cteSelecionadoUnico = selecionados.size === 1 ? ctes.find(n => n.chave === [...selecionados][0]) : null
  const podeImportar = !!cteSelecionadoUnico && !cteSelecionadoUnico.digitada && cteSelecionadoUnico.situacao === 'Autorizada'
  const podeImprimirDacte = !!cteSelecionadoUnico && cteSelecionadoUnico.xml_existe

  const ctesFiltrados = ctes.filter(n =>
    filtroDigitada === 'todas' ? true : filtroDigitada === 'sim' ? n.digitada : !n.digitada
  )

  const digitados = ctes.filter(n => n.digitada)
  const aDigitar = ctes.filter(n => !n.digitada)
  const totalDigitados = digitados.reduce((s, n) => s + parseFloat(n.valor), 0)
  const totalADigitar = aDigitar.reduce((s, n) => s + parseFloat(n.valor), 0)

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
        Manifestador de CT-e
      </h2>

      {/* Filtros */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        padding: '0.75rem 0.9rem', marginBottom: '1rem',
      }}>
        {campo('EMPRESA', (
          <div style={{ ...inp, width: 220, display: 'flex', alignItems: 'center', background: '#f3f4f6', color: '#6b7280' }}>
            {empresaAtual ? `${empresaAtual.empcod} - ${empresaAtual.empfan || empresaAtual.empnom}` : '—'}
          </div>
        ))}
        {campo('PERÍODO EMISSÃO DE', <input style={{ ...inp, width: 140 }} type="date" value={filtros.periodoDe}
          onChange={e => setFiltros(f => ({ ...f, periodoDe: e.target.value }))} />)}
        {campo('ATÉ', <input style={{ ...inp, width: 140 }} type="date" value={filtros.periodoAte}
          onChange={e => setFiltros(f => ({ ...f, periodoAte: e.target.value }))} />)}
        {campo('SITUAÇÃO', (
          <select style={{ ...inp, width: 130 }} value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}>
            <option value="">Todas</option>
            <option value="1">Autorizada</option>
            <option value="2">Denegada</option>
            <option value="3">Cancelada</option>
          </select>
        ))}
        {campo('DIGITADA', (
          <select style={{ ...inp, width: 130 }} value={filtroDigitada}
            onChange={e => setFiltroDigitada(e.target.value as 'todas' | 'sim' | 'nao')}>
            <option value="todas">Todas</option>
            <option value="sim">Digitadas</option>
            <option value="nao">Não digitadas</option>
          </select>
        ))}
        {campo('PAPEL DA EMPRESA', (
          <select style={{ ...inp, width: 140 }} value={filtros.papelEmpresa}
            onChange={e => setFiltros(f => ({ ...f, papelEmpresa: e.target.value }))}>
            <option value="">Todos</option>
            <option value="tomador">Tomador</option>
            <option value="destinatario">Destinatário</option>
          </select>
        ))}
        {campo('EMITENTE (CNPJ)', (
          <input style={{ ...inp, width: 150 }} value={filtros.emitente}
            placeholder="CNPJ da transportadora"
            onChange={e => setFiltros(f => ({ ...f, emitente: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        {campo('TOMADOR (CNPJ)', (
          <input style={{ ...inp, width: 150 }} value={filtros.tomador}
            placeholder="CNPJ do tomador"
            onChange={e => setFiltros(f => ({ ...f, tomador: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        {campo('CHAVE OU Nº DO CT-E', (
          <input style={{ ...inp, width: 340 }} value={filtros.chave}
            placeholder="Chave de 44 dígitos ou só o número"
            onChange={e => setFiltros(f => ({ ...f, chave: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        ))}
        <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
          style={{ height: 30, alignSelf: 'flex-end' }}>
          {carregando ? 'Buscando...' : 'Pesquisar'}
        </button>
        <button className="btn btn-sm" onClick={limpar} style={{ height: 30, alignSelf: 'flex-end', color: '#6b7280' }}>
          Limpar
        </button>
        {podeImprimirDacte && (
          <button
            className="btn btn-danger-solid btn-sm"
            onClick={imprimirDacteSelecionado}
            style={{ height: 30, alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconPdf /> Imprimir DACTE
          </button>
        )}
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {msgAtualizar && (
        <div className={`alert ${msgAtualizar.ok ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.75rem' }}>
          {msgAtualizar.texto}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
        <div style={{ flex: '0 1 auto', minWidth: 0 }}>
          {/* Grid */}
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table className="table" style={{ fontSize: '0.68rem', minWidth: 1250, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#eef2f7', borderBottom: '2px solid #cbd5e1' }}>
                  <th style={{ ...thBase, width: 30 }}></th>
                  <th style={{ ...thBase, width: 40, textAlign: 'center' }}>Emp</th>
                  <th style={{ ...thBase, width: 40, textAlign: 'center' }}>Ser</th>
                  <th style={{ ...thBase, width: 80 }}>Número</th>
                  <th style={{ ...thBase, width: 130 }}>Emitente</th>
                  <th style={{ ...thBase }}>Nome Emitente</th>
                  <th style={{ ...thBase, width: 130 }}>Tomador</th>
                  <th style={{ ...thBase }}>Nome Tomador</th>
                  <th style={{ ...thBase, width: 110 }}>Papel Tomador</th>
                  <th style={{ ...thBase, width: 100 }}>Papel Empresa</th>
                  <th style={{ ...thBase, width: 80 }}>Emissão</th>
                  <th style={{ ...thBase, width: 100, textAlign: 'right' }}>Valor R$</th>
                  <th style={{ ...thBase, width: 100 }}>Situação</th>
                  <th style={{ ...thBase, width: 82, textAlign: 'center' }}>Dig Fro</th>
                  <th style={{ ...thBase, width: 130 }}>Digitada em</th>
                  <th style={{ ...thBase, width: 80, textAlign: 'center' }}>Desacordo</th>
                  <th style={{ ...thBase, width: 50, textAlign: 'center' }}>XML</th>
                </tr>
              </thead>
              <tbody>
                {carregando && (
                  <tr><td colSpan={17} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
                )}
                {!carregando && ctesFiltrados.length === 0 && (
                  <tr><td colSpan={17} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhum CT-e encontrado</td></tr>
                )}
                {ctesFiltrados.map((n, idx) => (
                  <tr key={n.chave} style={{ borderBottom: '1px solid #e5e7eb', background: idx % 2 === 1 ? '#f0f2f5' : '#fff' }}>
                    <td style={{ textAlign: 'center', padding: '4px' }}>
                      <input type="checkbox" checked={selecionados.has(n.chave)} onChange={() => toggleSel(n.chave)} />
                    </td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>{n.emp}</td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>{n.serie}</td>
                    <td style={{ fontWeight: 700, padding: '4px' }}>
                      {n.digitada ? (
                        <span onClick={() => navigate(`/compras/consultas/notas?numero=${n.numero}`)}
                          style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                          title="Já digitada — clique para consultar a nota">
                          {n.numero}
                        </span>
                      ) : n.numero}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.74rem', whiteSpace: 'nowrap', padding: '4px' }}>{fmtCnpj(n.cnpj_emitente)}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '4px' }} title={n.nome_emitente}>
                      {n.nome_emitente}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.74rem', whiteSpace: 'nowrap', padding: '4px' }}>{n.cnpj_tomador ? fmtCnpj(n.cnpj_tomador) : ''}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '4px' }} title={n.nome_tomador}>
                      {n.nome_tomador}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', padding: '4px' }}>{n.papel_tomador}</td>
                    <td style={{ whiteSpace: 'nowrap', padding: '4px' }}>{n.papel_empresa}</td>
                    <td style={{ whiteSpace: 'nowrap', padding: '4px' }}>{fmtData(n.emissao)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, padding: '4px' }}>{fmtMoeda(n.valor)}</td>
                    <td style={{ padding: '4px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                        fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap',
                        background: SIT_BG[n.situacao] ?? '#f3f4f6',
                        color: SIT_COLOR[n.situacao] ?? '#374151',
                      }}>
                        {n.situacao}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>{selo(n.digitada)}</td>
                    <td style={{ whiteSpace: 'nowrap', color: '#6b7280', padding: '4px' }}>{n.digitada && n.digitada_em ? fmtDataHora(n.digitada_em) : ''}</td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>{selo(n.desacordo)}</td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>{selo(n.xml_existe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Resumo */}
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap', gap: '1.75rem',
            border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px',
            background: '#f8fafc', padding: '0.85rem 1.25rem',
          }}>
            {stat('CT-es Digitados', digitados.length, '#2563eb')}
            <div style={dividerStyle} />
            {stat('Total Digitados R$', fmtMoeda(totalDigitados), '#2563eb')}
            <div style={dividerStyle} />
            {stat('CT-es a Digitar', aDigitar.length, aDigitar.length > 0 ? '#b45309' : '#0f172a')}
            <div style={dividerStyle} />
            {stat('Total a Digitar R$', fmtMoeda(totalADigitar), aDigitar.length > 0 ? '#b45309' : '#0f172a')}
          </div>

          {/* Chave do CT-e */}
          <div style={{
            marginTop: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 8,
            background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
            padding: '0.9rem 1rem', textAlign: 'center',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <IconChave />
              <input
                style={{ ...inp, width: 400, fontFamily: 'monospace' }}
                value={chaveConsulta}
                onChange={e => setChaveConsulta(e.target.value)}
                placeholder="Chave de acesso do CT-e ou CT-e OS (44 dígitos)"
              />
            </div>
            <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
              Tecle{' '}
              <span style={{
                display: 'inline-block', padding: '1px 7px', borderRadius: 4,
                background: '#f1f5f9', border: '1px solid #e2e8f0',
                fontFamily: 'monospace', fontWeight: 700, color: '#475569', fontSize: '0.72rem',
              }}>
                Enter ↵
              </span>{' '}
              para visualizar os eventos do CT-e
            </span>
          </div>
        </div>

        {/* Barra de ações (Atualizar Lista, Importar XML e Disponibilidade já funcionam; demais ainda só visuais) */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: '0.6rem',
          padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
        }}>
          <ToolbarBtn
            title={atualizando ? 'Consultando SEFAZ...' : 'Atualizar Lista'}
            bg="#334155" fg="#fff" border="#334155"
            disabled={atualizando}
            onClick={atualizarLista}
          >
            <IconAtualizar />
          </ToolbarBtn>
          <ToolbarBtn title="Prestação de Serviço em Desacordo" bg="#b91c1c" fg="#fff" border="#b91c1c"><IconDesacordo /></ToolbarBtn>
          <ToolbarBtn
            title="Abrir o portal do CT-e pra consultar/baixar o XML pela chave"
            bg="#334155" fg="#fff" border="#334155"
            onClick={() => window.open('https://www.cte.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=completa&tipoConteudo=mCK/KoCqru0=', '_blank')}
          ><IconDownload /></ToolbarBtn>
          <ToolbarBtn
            title={podeImportar ? 'Importar XML pro Lançamento de CT-e' : 'Selecione um CT-e Autorizado e ainda não digitado'}
            bg="#334155" fg="#fff" border="#334155" destaque={podeImportar}
            onClick={importarSelecionado}
          ><IconImportar /></ToolbarBtn>
          <ToolbarBtn
            title={consultandoStatus ? 'Consultando SEFAZ-RS...' : 'Consulta Disponibilidade (SEFAZ-RS, CT-e)'}
            bg="#334155" fg="#fff" border="#334155" disabled={consultandoStatus}
            onClick={consultarDisponibilidade}
          ><IconDisponibilidade /></ToolbarBtn>
        </div>
      </div>
    </div>
  )
}
